import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { systemClock, businessDate, daysBetween, isBefore } from '@sakhtar/calendar';
import {
  V2Card,
  V2Badge,
  V2Button,
  V2DataGrid,
  V2EmptyState,
  V2FormGrid,
  V2FormField,
  V2FormActions,
  Icon,
  LoadingState,
  StatePanel,
  Combobox,
  type ComboboxOption,
  type V2BadgeTone,
  type V2TableColumn,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPost, ApiError } from '../../lib/apiClient.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { PartyLink } from './PartyLink.js';
import type { LeadListResponse, Quote, QuoteListResponse } from '../../lib/types/crm.js';
import { fetchNextDocumentRef } from '../../lib/suggestedCode.js';
import type { CatalogListResponse, MasterDataListResponse } from '../../lib/types/masterData.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { amountColumnHeader, formatRialFa } from '../../lib/finance-display.js';
import { useAmountDisplay, useTenantSettings } from '../../lib/tenantSettings.js';
import { vatEnabled } from '../../lib/types/tenantSettings.js';
import {
  EMPTY_QUOTE_LINE,
  QuoteLinesEditor,
  payloadLines,
  type QuoteLineDraft,
} from './QuoteLinesEditor.js';

const STATUS_TONE: Record<string, V2BadgeTone> = {
  پیش‌نویس: 'neutral',
  ارسال‌شده: 'info',
  پذیرفته‌شده: 'success',
  'رد شده': 'danger',
  منقضی: 'neutral',
  'نزدیک پایان اعتبار': 'warn',
};

/** Not a backend status ('draft'|'sent'|'accepted'|'rejected'|'expired' are
 * the only real ones) — a computed presentation over the real `validUntil`
 * field, same pattern as ActivitiesPage.tsx's "عقب‌افتاده". Threshold (7
 * days) isn't specified anywhere in the design text itself; chosen to
 * match the existing subscription trial-banner's own "warn" tier for
 * consistency — a recorded interpretation, not a literal design value. */
const EXPIRING_SOON_THRESHOLD_DAYS = 7;

function isExpiringSoon(row: Quote): boolean {
  if (row.statusLabel !== 'ارسال‌شده' || !row.validUntil) return false;
  const today = businessDate(systemClock.today());
  const validUntil = businessDate(row.validUntil);
  if (isBefore(validUntil, today)) return false;
  return daysBetween(today, validUntil) <= EXPIRING_SOON_THRESHOLD_DAYS;
}

interface Row extends Quote {
  readonly id: string;
}

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Sales CRM.dc.html §05 QUOTES — list + create
 * archetype: ENTITY_LIST + create form
 * status: KIT_CONFORMANT.
 *
 * BUG-07 reversal: a prior pass here correctly found no line-item, tax, or
 * lead-link backing at the time and built only a single-total
 * shell, documenting the gap plainly. The aryapakhsh demo QA pass named
 * that gap a P0 contract violation, and the backend now has all three
 * (packages/modules/crm-sales/src/sales-lines.ts's discount/tax columns,
 * crm_quote.lead_id) — this form now posts a real itemized quote:
 * product/description/quantity/UOM/unit price/discount/tax per line,
 * server-computed totals only (the client never computes lineTotalRial or
 * taxAmountRial itself — it only sends the inputs and renders what the
 * server returns).
 * state reference: ready · empty · permission_denied · network_error
 */
export function QuotesPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const showVat = vatEnabled(useTenantSettings());
  const { unit } = useAmountDisplay();
  const [partyRef, setPartyRef] = useState('');
  const [searchParams] = useSearchParams();
  const [leadRef, setLeadRef] = useState(searchParams.get('lead') ?? '');
  const [validUntil, setValidUntil] = useState('');
  const [lines, setLines] = useState<QuoteLineDraft[]>([{ ...EMPTY_QUOTE_LINE }]);
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  // Preview of the number the next quote will get (issued by the server at save).
  const [nextRef, setNextRef] = useState<string | null>(null);

  const quotes = useApiGet<QuoteListResponse>(
    slug ? `${base}/api/v1/sales/quotes?limit=200` : null,
    [slug, refreshKey],
  );
  // BUG-10: only customers belong in a pre-invoice's party picker.
  const parties = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/parties?role=customer&limit=200` : null,
    [slug],
  );
  // Open leads only: a won or lost lead has nothing left to quote.
  const leads = useApiGet<LeadListResponse>(
    slug ? `${base}/api/v1/crm/leads?view=open&limit=100` : null,
    [slug],
  );

  // Arriving from a lead («پیش‌فاکتور»): carry its customer along when it has one.
  useEffect(() => {
    if (!leadRef || partyRef || leads.status !== 'ready') return;
    const customer = leads.data.leads.find((lead) => lead.ref === leadRef)?.deal.customer;
    if (customer) setPartyRef(customer.ref);
  }, [leads, leadRef, partyRef]);
  const products = useApiGet<CatalogListResponse>(
    slug ? `${base}/api/v1/master-data/product-catalog?status=active&limit=200` : null,
    [slug],
  );
  const taxes = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/taxes?status=active&limit=200` : null,
    [slug],
  );

  const partyOptions = useMemo<ComboboxOption[]>(() => {
    if (parties.status !== 'ready') return [];
    return parties.data.items.map((p) => ({
      value: String(p.code),
      label: `${p.code} — ${p.name ?? p.code}`,
    }));
  }, [parties]);

  const filtered = useMemo(() => {
    if (quotes.status !== 'ready') return [];
    const q = query.trim().toLowerCase();
    if (!q) return quotes.data.quotes;
    return quotes.data.quotes.filter(
      (row) => row.ref.toLowerCase().includes(q) || (row.partyRef ?? '').toLowerCase().includes(q),
    );
  }, [quotes, query]);

  if (!slug) return null;

  function resetForm() {
    setPartyRef('');
    setLeadRef('');
    setValidUntil('');
    setLines([{ ...EMPTY_QUOTE_LINE }]);
    setFieldErrors({});
  }

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    void fetchNextDocumentRef(base, 'quote').then((ref) => {
      if (!cancelled) setNextRef(ref);
    });
    return () => {
      cancelled = true;
    };
  }, [slug, base, refreshKey]);

  async function onCreate(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    setFieldErrors({});
    const validLines = payloadLines(lines);
    if (validLines.length === 0) {
      setError('حداقل یک قلم با کالا، مقدار و واحد لازم است.');
      setSubmitting(false);
      return;
    }
    try {
      await apiPost(`${base}/api/v1/sales/quotes`, {
        partyRef: partyRef || undefined,
        leadRef: leadRef || undefined,
        validUntil: validUntil || undefined,
        lines: validLines,
      });
      resetForm();
      setRefreshKey((k) => k + 1);
    } catch (err) {
      if (err instanceof ApiError) {
        const details = err.envelope.error.details;
        const newFieldErrors: Record<string, string> = {};
        if (details && details.length > 0) {
          for (const item of details) {
            newFieldErrors[item.path] = item.message;
          }
        }
        if (err.envelope.error.code === 'PARTY_ROLE_INVALID' && !newFieldErrors['partyRef']) {
          newFieldErrors['partyRef'] = err.envelope.error.message;
        }
        setFieldErrors(newFieldErrors);
        setError(err.envelope.error.message);
      } else {
        setError('ثبت پیش‌فاکتور ناموفق بود.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  const productOptions = products.status === 'ready' ? products.data.items : [];
  const taxOptions = taxes.status === 'ready' ? taxes.data.items : [];

  const columns: readonly V2TableColumn<Row>[] = [
    {
      key: 'ref',
      header: 'شماره',
      render: (row) => (
        <Link
          to={`${base}/crm/quotes/${row.ref}`}
          className="ltr"
          style={{ fontFamily: 'var(--sk-font-mono)', fontSize: 13 }}
        >
          {row.ref}
        </Link>
      ),
    },
    {
      key: 'partyRef',
      header: 'طرف‌حساب',
      render: (row) =>
        row.partyRef ? (
          <PartyLink base={base} partyRef={row.partyRef} inRow>
            {row.partyRef}
          </PartyLink>
        ) : (
          '—'
        ),
    },
    {
      key: 'totalRial',
      header: amountColumnHeader('مبلغ کل', unit),
      align: 'end',
      render: (row) => <span className="sk-num">{formatRialFa(row.totalRial, unit)}</span>,
    },
    {
      key: 'validUntil',
      header: 'اعتبار تا',
      render: (row) => <span className="sk-num">{displayJalaliDate(row.validUntil)}</span>,
    },
    {
      key: 'statusLabel',
      header: 'وضعیت',
      render: (row) => {
        const label = isExpiringSoon(row) ? 'نزدیک پایان اعتبار' : row.statusLabel;
        return <V2Badge tone={STATUS_TONE[label] ?? 'neutral'}>{label}</V2Badge>;
      },
    },
    {
      key: 'print',
      header: '',
      align: 'end',
      render: (row) => (
        <Link
          to={`${base}/crm/quotes/${row.ref}/print`}
          className="sk-btn sk-btn--ghost sk-btn--icon sk-btn--sm"
          aria-label="چاپ پیش‌فاکتور"
          title="چاپ پیش‌فاکتور"
        >
          <Icon name="invoice" size="sm" />
        </Link>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <V2Card>
        <form
          onSubmit={onCreate}
          style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}
        >
          <V2FormGrid>
            {nextRef ? (
              <V2FormField
                label="شماره پیش‌فاکتور"
                width="short"
                hint="پیش‌نمایش؛ شمارهٔ رسمی هنگام ثبت توسط سیستم صادر می‌شود"
              >
                <input className="sk-input ltr" value={nextRef} readOnly aria-readonly="true" />
              </V2FormField>
            ) : null}
            <V2FormField label="طرف‌حساب" width="wide" error={fieldErrors['partyRef']}>
              <Combobox
                options={partyOptions}
                value={partyRef}
                onValueChange={(val) => {
                  setPartyRef(val);
                  setFieldErrors((prev) => {
                    const next = { ...prev };
                    delete next['partyRef'];
                    return next;
                  });
                }}
                placeholder="جست‌وجوی طرف‌حساب (کد یا نام)..."
                disabled={parties.status === 'loading'}
              />
            </V2FormField>
            <V2FormField label="سرنخ مرتبط" width="wide">
              <select
                className="sk-select"
                value={leadRef}
                disabled={leads.status !== 'ready'}
                onChange={(e) => setLeadRef(e.target.value)}
              >
                <option value="">— بدون سرنخ —</option>
                {leads.status === 'ready'
                  ? leads.data.leads.map((lead) => (
                      <option key={lead.ref} value={lead.ref}>
                        {lead.displayName} ({lead.ref})
                      </option>
                    ))
                  : null}
              </select>
            </V2FormField>
            <V2FormField label="اعتبار تا" width="medium">
              <JalaliDateInput value={validUntil} onChange={setValidUntil} />
            </V2FormField>
          </V2FormGrid>

          <QuoteLinesEditor
            base={base}
            lines={lines}
            onChange={setLines}
            products={productOptions}
            taxes={taxOptions}
            showVat={showVat}
            unitLabel="ریال"
          />

          <V2FormActions>
            {error ? (
              <span style={{ fontSize: 13, color: 'var(--sk-danger)', marginInlineEnd: 'auto' }}>
                {error}
              </span>
            ) : null}
            <V2Button type="submit" variant="primary" icon="plus" disabled={submitting}>
              {submitting ? 'در حال ثبت…' : 'پیش‌فاکتور جدید'}
            </V2Button>
          </V2FormActions>
        </form>
      </V2Card>

      {quotes.status === 'loading' ? <LoadingState /> : null}
      {quotes.status === 'error' ? (
        <StatePanel
          variant={quotes.error.status === 403 ? 'permission_denied' : 'network_error'}
          title={quotes.error.status === 403 ? 'دسترسی ندارید' : 'فهرست قابل نمایش نیست'}
        />
      ) : null}

      {quotes.status === 'ready' ? (
        <V2DataGrid
          icon="invoice"
          title="پیش‌فاکتورها"
          subtitle={`${(!query.trim() && (quotes.data as any)?.total !== undefined
            ? (quotes.data as any).total
            : filtered.length
          ).toLocaleString('fa-IR')} پیش‌فاکتور در دسترس شما`}
          search={{ value: query, onChange: setQuery, placeholder: 'شماره یا نام طرف‌حساب' }}
          columns={columns}
          rows={filtered.map((q) => ({ ...q, id: q.ref }))}
          rowKey={(q) => q.id}
          zebra
          emptyState={
            <V2EmptyState
              icon="invoice"
              title={query ? 'پیش‌فاکتوری با این جست‌وجو پیدا نشد' : 'پیش‌فاکتوری ثبت نشده'}
              description={
                query ? undefined : 'پیش‌فاکتور جدید را از فرم بالا برای یک طرف‌حساب ثبت کنید.'
              }
              compact
            />
          }
        />
      ) : null}
    </div>
  );
}
